import { useContext, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { IntlContext } from '@/i18n';
import { getLocalizedPartnerType } from '@/i18n/getLocalizedPartnerType';
import { IPartner } from '@/modules/partner/model';
import { getLocalizedTaxProfile } from '@/modules/taxProfile/i18n/getLocalizedTaxProfile';
import { CountryCode } from '@/shared/models/country-code';
import { DescriptionList } from '@/shared/view/DescriptionList';

import { messages } from './selfEmployedMessages';
import s from './styles.module.css';
import { useSelfEmployedInfo } from './us/PayoutsInfo';

interface Props {
  partner: IPartner;
  className: string;
  onEdit?: () => void;
}

const mapToSelfEmployedTaxInfo = (partner: IPartner, intl: IntlShape) => ({
  partnerType: partner.partnerType ? getLocalizedPartnerType(partner.partnerType, intl) : '',
  taxProfile: getLocalizedTaxProfile(partner.taxProfile, intl),
  vatRegNo: partner.vatRegNo,
  ssn: partner.ssn,
});

type TTitleLine = [keyof ReturnType<typeof mapToSelfEmployedTaxInfo>, string];

const SelfEmployedInfo = ({ className, partner, onEdit }: Props) => {
  const intl = useIntl();
  const { region } = useContext(IntlContext);
  const usTitles = useSelfEmployedInfo({ region, partner });

  const showForRegion = region === CountryCode.GreatBritain || region === CountryCode.UnitedStates;

  const titles = useMemo((): TTitleLine[] => {
    const allTitles: TTitleLine[] = [
      ['partnerType', intl.formatMessage(messages.companyTypeTitle)],
      ['taxProfile', intl.formatMessage(messages.taxProfileTitle)],
      ['vatRegNo', intl.formatMessage(messages.vatRegNoTitle)],
    ];

    if (usTitles.length) {
      allTitles.push(...usTitles);
    }

    return allTitles;
  }, [intl, usTitles]);

  const values = useMemo(() => mapToSelfEmployedTaxInfo(partner, intl), [partner, intl]);

  if (!titles.length || !showForRegion) {
    return null;
  }

  const action =
    onEdit && region === CountryCode.GreatBritain ? (
      <span onClick={onEdit} className={s.editButton}>
        {intl.formatMessage(messages.editAction)}
      </span>
    ) : undefined;

  return (
    <DescriptionList
      title={intl.formatMessage(messages.title)}
      className={className}
      values={values}
      titles={titles}
      action={action}
    />
  );
};

export { SelfEmployedInfo };
